import { cva, type VariantProps } from 'class-variance-authority';

import { buttonVariants } from '@/hen-ui/atomics/Button/button.variants';

/** Unselected looks, in the order authors see them. */
export const TAB_BUTTON_APPEARANCES = ['outline', 'filled'] as const;

export type TabButtonAppearance = (typeof TAB_BUTTON_APPEARANCES)[number];

/**
 * Selected look, keyed off the `data-state="active"` Radix sets on the
 * selected trigger. Each hover, pressed and disabled state is restated under
 * `data-[state=active]:` so it outranks the unselected state classes instead
 * of depending on stylesheet order.
 */
const SELECTED_STYLE = [
  'data-[state=active]:bg-tab-button-selected data-[state=active]:text-tab-button-selected',
  'data-[state=active]:border-2 data-[state=active]:border-transparent',
  'data-[state=active]:hover:bg-tab-button-selected-hover data-[state=active]:hover:text-tab-button-selected',
  'data-[state=active]:active:bg-tab-button-selected-active data-[state=active]:active:text-tab-button-selected',
  'data-[state=active]:disabled:bg-button-secondary-disabled data-[state=active]:disabled:text-button-white-disabled data-[state=active]:disabled:border-transparent',
];

/**
 * TabButton classes: Button's `sm` structure (radius, type, padding, focus
 * ring, press scale, transitions, disabled) with no Button color variant, and
 * tab state colors from the `--cds-tab-button-*` tokens in
 * `constants/tabButtonTheme.ts`.
 *
 * A pill stays as wide as its one-line label so a row that does not fit
 * scrolls instead of wrapping.
 */
export const tabButtonVariants = cva(
  [
    buttonVariants({ variant: null, size: 'sm' }),
    'h-button-sm min-h-button-sm shrink-0 whitespace-nowrap',
    'focus-visible:outline-tab-button-focus focus-visible:outline-1',
    'disabled:opacity-tab-button-disabled',
    ...SELECTED_STYLE,
  ],
  {
    variants: {
      appearance: {
        outline: [
          'bg-tab-button-outline text-tab-button-outline',
          'border-button border-tab-button-outline',
          'hover:bg-tab-button-outline-hover hover:text-tab-button-outline-hover',
          'active:bg-tab-button-outline-active active:text-tab-button-outline-active',
          'disabled:bg-button-disabled disabled:text-button-black-disabled disabled:border-button-gray-disabled',
          'disabled:hover:bg-button-disabled-hover',
        ],
        filled: [
          'bg-tab-button-filled text-tab-button-filled',
          'border-2 border-transparent',
          'hover:bg-tab-button-filled-hover hover:text-tab-button-filled-hover',
          'active:bg-tab-button-filled-active active:text-tab-button-filled-active',
        ],
      } satisfies Record<TabButtonAppearance, string[]>,
    },
    defaultVariants: {
      appearance: 'outline',
    },
  },
);

export type TabButtonVariantProps = VariantProps<typeof tabButtonVariants>;
